
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function HoutBayPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Hout Bay Painters Cape Town | Coastal Property Painting Services | Marine-Grade Protection";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Hout Bay coastal properties. Marine-grade protection and expert craftsmanship for homes facing Atlantic Ocean conditions.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Hout Bay painters, coastal property painting, marine protection, Hout Bay homes, Cape Town coastal painters');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/hout-bay`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Hout Bay Painters - Coastal Property Painting Services",
      "description": "Professional painting services for Hout Bay coastal properties with marine-grade protection systems.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/hout-bay`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Hout Bay",
        "description": "Professional coastal property painting services for Hout Bay's unique marine environment.",
        "areaServed": "Hout Bay, Cape Town",
        "serviceType": "Coastal Property Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Hout Bay')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Coastal Home Painting',
      description: 'Specialized painting for Hout Bay homes with marine-grade protection against salt air, moisture, and coastal weather conditions.',
      price: 'From R16,000',
      features: ['Marine-grade coatings', 'Salt air protection', 'Moisture resistance', 'UV protection', 'Coastal durability']
    },
    {
      icon: 'ri-building-line',
      title: 'Harbour Property Painting',
      description: 'Expert painting services for properties near Hout Bay harbour, including specialized treatments for high-moisture environments.',
      price: 'From R14,000',
      features: ['Harbour environment expertise', 'Anti-corrosion treatments', 'Moisture barriers', 'Quick-dry systems', 'Weather resistance']
    },
    {
      icon: 'ri-palette-line',
      title: 'Mountain View Properties',
      description: 'Premium painting for Hout Bay properties with mountain views, balancing coastal protection with aesthetic appeal.',
      price: 'From R12,000',
      features: ['Scenic integration', 'Weather protection', 'Color consultation', 'Landscape harmony', 'Premium finishes']
    }
  ];

  const features = [
    {
      title: 'Marine Environment Expertise',
      description: 'Specialized knowledge in painting coastal properties exposed to Hout Bay\'s unique marine environment, including salt spray and high humidity conditions.'
    },
    {
      title: 'Harbour Area Specialists',
      description: 'Expert experience working near Hout Bay harbour with understanding of the additional challenges posed by maritime activities and increased moisture levels.'
    },
    {
      title: 'Mountain-Coast Integration',
      description: 'Skilled in creating paint schemes that complement both Hout Bay\'s dramatic mountain backdrop and coastal setting for harmonious aesthetic results.'
    },
    {
      title: 'Weather-Resistant Solutions',
      description: 'Advanced coating systems designed to withstand Hout Bay\'s variable weather patterns, from coastal storms to mountain winds.'
    }
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Coastal Assessment',
      description: 'Comprehensive evaluation of your Hout Bay property\'s exposure to marine conditions and specific environmental challenges.'
    },
    {
      step: 2,
      title: 'Marine Preparation',
      description: 'Specialized surface preparation including salt damage treatment and moisture barrier application for coastal protection.'
    },
    {
      step: 3,
      title: 'Expert Application',
      description: 'Professional application using marine-grade materials and techniques developed specifically for Hout Bay\'s coastal environment.'
    },
    {
      step: 4,
      title: 'Quality Assurance',
      description: 'Thorough inspection ensuring optimal protection against Hout Bay\'s unique coastal challenges and weather conditions.'
    }
  ];

  const testimonials = [
    {
      name: 'Michael Thompson',
      property: 'Harbour View Home',
      rating: 5,
      text: 'Excellent work on our Hout Bay home. The team understood the coastal challenges and delivered a finish that has withstood the marine environment beautifully. Highly recommended for coastal properties.',
      image: 'https://public.readdy.ai/ai/img_res/d3f42325b0c5e1a6dcb230da540284f4.jpg'
    },
    {
      name: 'Lisa Roberts',
      property: 'Mountain View Cottage',
      rating: 5,
      text: 'Outstanding service for our Hout Bay cottage. The marine-grade finish has performed excellently and the color choice perfectly complements our mountain and sea views.',
      image: 'https://readdy.ai/api/search-image?query=Woman%20homeowner%20at%20Hout%20Bay%20cottage%20with%20mountain%20backdrop%2C%20satisfied%20client%20portrait%2C%20coastal%20property%20setting&width=80&height=80&seq=houtbay2&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Harbour Front Residence',
      description: 'Complete exterior renovation with specialized marine coatings for a property directly facing Hout Bay harbour, providing maximum protection against salt spray.',
      image: 'https://readdy.ai/api/search-image?query=Hout%20Bay%20harbour%20front%20home%20with%20mountain%20backdrop%2C%20freshly%20painted%20exterior%2C%20coastal%20property%2C%20professional%20painting%20work&width=400&height=300&seq=houtbay3&orientation=landscape'
    },
    {
      title: 'Mountain Slope Villa',
      description: 'Premium painting project for a hillside property with panoramic views, featuring weather-resistant finishes and color schemes that complement the natural setting.',
      image: 'https://readdy.ai/api/search-image?query=Hout%20Bay%20hillside%20villa%20with%20mountain%20and%20ocean%20views%2C%20newly%20painted%20exterior%2C%20scenic%20coastal%20property&width=400&height=300&seq=houtbay4&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20coastal%20property%20in%20Hout%20Bay%20with%20harbour%20and%20mountain%20views%2C%20painting%20equipment%2C%20scaffolding%2C%20marine%20environment%2C%20sunny%20day&width=1920&height=1080&seq=houtbay_hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Hout Bay
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert coastal property painting services for Hout Bay. Marine-grade protection and professional craftsmanship for homes in this unique harbour setting with over 20 years of experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Hout Bay Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Expert Painting Services for Hout Bay
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Hout Bay is a picturesque coastal suburb nestled between dramatic mountains and the Atlantic Ocean, featuring a working harbour and diverse residential properties. This unique setting presents specific challenges that require specialized painting expertise and marine-grade protection systems.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our experienced team understands Hout Bay's distinctive environment, from the salt-laden air near the harbour to the mountain winds that sweep through the valley. We've developed specialized techniques and use premium materials specifically chosen for this challenging coastal location.
              </p>
              <p className="text-lg text-gray-600">
                Whether your property overlooks the harbour, nestles against the mountainside, or sits in the valley between, our Hout Bay painting specialists deliver results that provide superior protection while enhancing the natural beauty of this remarkable coastal community.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Hout%20Bay%20coastal%20community%20with%20harbour%2C%20mountain%20backdrop%2C%20residential%20properties%2C%20scenic%20coastal%20suburb%20setting&width=600&height=400&seq=houtbay5&orientation=landscape"
                alt="Hout Bay Coastal Community"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Hout Bay Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized coastal painting solutions designed for Hout Bay's unique marine and mountain environment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Hout Bay?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized expertise in coastal and harbour environment painting with marine-grade protection systems.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Hout Bay Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A specialized approach developed for Hout Bay's unique coastal and harbour environment challenges.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold" style={{backgroundColor: '#ffff00'}}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Hout Bay Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our recent coastal painting projects across Hout Bay's diverse property landscape.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Testimonials from satisfied Hout Bay property owners.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready for Professional Painting in Hout Bay?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's coastal property painting specialists. Experience marine-grade protection and expert craftsmanship for your Hout Bay home.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="/contact"
              className="bg-teal-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </a>
            <a
              href="mailto:Get a quote"
              className="bg-black text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
